import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Avatar,
  IconButton,
  Input,
  useOutsideClick,
} from '@chakra-ui/react';
import { FiEdit3, FiCheck, FiX } from 'react-icons/fi';
import { fetchAdmins, updateCardAssignee } from '../../../../../ProjectBoardServices/BoardModalAPI/RightModalAPI/InfoTabAPI/AssigneeAPI';

const Assignee = ({ task, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState(task?.assignee || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);

  const dropdownRef = useRef();
  const searchInputRef = useRef();

  useOutsideClick({
    ref: dropdownRef,
    handler: () => {
      setIsEditing(false);
      setSearchValue('');
    },
  });

  useEffect(() => {
    if (isEditing && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isEditing]);

  const loadAdmins = async () => {
    setIsLoadingAdmins(true);
    try {
      // ✅ Service function use kar rahe hain
      const response = await fetchAdmins(0);

      if (response.data && response.data.admins) {
        setAdmins(response.data.admins);
      }
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      alert('Failed to load team members. Please try again.');
    } finally {
      setIsLoadingAdmins(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    if (admins.length === 0) {
      loadAdmins();
    }
  };

  const filteredAdmins = admins.filter((admin) => {
    const fullName = `${admin.admin_firstname} ${admin.admin_lastname}`.toLowerCase();
    const email = admin.admin_email?.toLowerCase() || '';
    const search = searchValue.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  const handleAssigneeSelect = async (admin) => {
    setIsLoading(true);
    try {
      // ✅ Service function use kar rahe hain
      const response = await updateCardAssignee(task.id, admin.admin_id);

      if (response.data) {
        const newAssignee = {
          id: admin.admin_id,
          name: `${admin.admin_firstname} ${admin.admin_lastname}`,
          email: admin.admin_email,
        };

        setSelectedAssignee(newAssignee);
        setIsEditing(false);
        setSearchValue('');

        if (onUpdate) {
          onUpdate({ ...task, assignee: newAssignee });
        }
      }
    } catch (error) {
      console.error('Failed to update assignee:', error);
      alert('Failed to update assignee. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAssignee = async () => {
    setIsLoading(true);
    try {
      // ✅ Service function use kar rahe hain (null pass karke remove)
      const response = await updateCardAssignee(task.id, null);

      if (response.data) {
        setSelectedAssignee(null);
        setIsEditing(false);

        if (onUpdate) {
          onUpdate({ ...task, assignee: null });
        }
      }
    } catch (error) {
      console.error('Failed to remove assignee:', error);
      alert('Failed to remove assignee. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box ref={dropdownRef} position="relative">
      <Text fontSize="12px" fontWeight="600" color="#6B778C" mb={2} textTransform="uppercase">
        ASSIGNEE
      </Text>

      {isEditing ? (
        <Box>
          <Input
            ref={searchInputRef}
            placeholder="Search team members..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            size="sm"
            mb={2}
          />

          {isLoadingAdmins ? (
            <Text fontSize="14px" color="#6B778C" p={2}>
              Loading...
            </Text>
          ) : (
            <Box
              border="1px solid #DFE1E6"
              borderRadius="4px"
              maxH="200px"
              overflowY="auto"
              bg="white"
            >
              {/* {selectedAssignee && (
                <Box
                  px={3}
                  py={2}
                  fontSize="14px"
                  cursor="pointer"
                  borderBottom="1px solid #EDEFF2"
                  color="red.500"
                  _hover={{ bg: '#FFEBEE' }}
                  onClick={handleRemoveAssignee}
                >
                  Remove current assignee
                </Box>
              )} */}

              {filteredAdmins.map((admin) => (
                <Box
                  key={admin.admin_id}
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: '#EDEFF2' }}
                  onClick={() => handleAssigneeSelect(admin)}
                >
                  <HStack spacing={2}>
                    <Avatar
                      size="xs"
                      name={`${admin.admin_firstname} ${admin.admin_lastname}`}
                    />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="14px" fontWeight="500">
                        {admin.admin_firstname} {admin.admin_lastname}
                      </Text>
                      <Text fontSize="12px" color="#6B778C">
                        {admin.admin_email}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
              {filteredAdmins.length === 0 && (
                <Box px={3} py={2} fontSize="14px" color="gray.500">
                  No members found
                </Box>
              )}
            </Box>
          )}

          <HStack mt={2} spacing={2}>
            <IconButton
              icon={<FiX />}
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setSearchValue('');
              }}
              aria-label="Cancel"
            />
          </HStack>
        </Box>
      ) : (
        <VStack align="stretch" spacing={2}>
          {selectedAssignee ? (
            <HStack spacing={2} justify="space-between">
              <HStack spacing={2}>
                <Avatar size="sm" name={selectedAssignee.name} w="32px" h="32px" />
                <VStack align="start" spacing={0}>
                  <Text fontSize="14px" color="#172B4D" fontWeight="500">
                    {selectedAssignee.name}
                  </Text>
                  <Text fontSize="12px" color="#6B778C">
                    {selectedAssignee.email}
                  </Text>
                </VStack>
              </HStack>
              <IconButton
                icon={<FiEdit3 />}
                size="xs"
                variant="ghost"
                onClick={handleEdit}
                isLoading={isLoading}
                aria-label="Edit assignee"
              />
            </HStack>
          ) : (
            <HStack justify="space-between">
              <Text fontSize="14px" color="#6B778C">
                No assignee assigned.
              </Text>
              <IconButton
                icon={<FiEdit3 />}
                size="xs"
                variant="ghost"
                onClick={handleEdit}
                isLoading={isLoading}
                aria-label="Assign member"
              />
            </HStack>
          )}
        </VStack>
      )}
    </Box>
  );
};

export default Assignee;
